import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Plus, RefreshCw, Tag } from 'lucide-react';
import Card from '../../components/Card';
import Table from '../../components/Table';
import Badge from '../../components/Badge';
import Button from '../../components/Button';
import Select from '../../components/Select';
import LoadingSpinner from '../../components/LoadingSpinner';
import { getSupportTickets, getSupportTicketStats, getSupportTicketTags } from '../../utils/api';
import { formatDate, formatTimeAgo } from '../../utils/helpers';

const getStatusBadgeVariant = (status) => {
    switch (status) {
        case 'open':
            return 'warning';
        case 'in_progress':
            return 'info';
        case 'resolved':
            return 'success';
        case 'closed':
            return 'primary';
        default:
            return 'primary';
    }
};

const getPriorityBadgeVariant = (priority) => {
    switch (priority) {
        case 'high':
            return 'danger';
        case 'medium':
            return 'warning';
        case 'low':
            return 'info';
        default:
            return 'primary';
    }
};

const getStatusLabel = (status) => {
    switch (status) {
        case 'open':
            return 'Open';
        case 'in_progress':
            return 'In Progress';
        case 'resolved':
            return 'Resolved';
        case 'closed':
            return 'Closed';
        default:
            return status;
    }
};

export default function TicketList() {
    const navigate = useNavigate();
    const [status, setStatus] = useState('open');
    const [selectedTag, setSelectedTag] = useState('');
    const [createdFrom, setCreatedFrom] = useState('');

    // Fetch ticket stats
    const { data: statsData } = useQuery({
        queryKey: ['support-ticket-stats'],
        queryFn: getSupportTicketStats,
    });

    // Fetch tags for filter
    const { data: tagsData } = useQuery({
        queryKey: ['support-ticket-tags'],
        queryFn: getSupportTicketTags,
    });

    // Fetch tickets
    const { data: ticketsData, isLoading, refetch } = useQuery({
        queryKey: ['support-tickets', status, selectedTag, createdFrom],
        queryFn: () => getSupportTickets({
            status: status || undefined,
            tags: selectedTag ? [selectedTag] : undefined,
            createdFrom: createdFrom || undefined,
        }),
    });

    const stats = statsData?.data || { byStatus: { open: 0, in_progress: 0, resolved: 0, closed: 0 } };
    const tags = tagsData?.data || [];
    const tickets = ticketsData?.data?.tickets || [];

    const columns = [
        {
            header: 'Subject',
            accessor: 'subject',
            render: (ticket) => (
                <div className="max-w-xs">
                    <p className="font-medium truncate">{ticket.subject}</p>
                    <p className="text-xs text-muted-light dark:text-muted-dark truncate">
                        {ticket.description?.substring(0, 50)}...
                    </p>
                </div>
            ),
        },
        {
            header: 'User',
            accessor: 'targetUser',
            render: (ticket) => (
                <div className="flex items-center gap-2">
                    {ticket.targetUser?.profilePicture ? (
                        <img
                            src={ticket.targetUser.profilePicture}
                            alt=""
                            className="w-8 h-8 rounded-full object-cover"
                        />
                    ) : (
                        <div className="w-8 h-8 rounded-full bg-accent-light dark:bg-accent-dark flex items-center justify-center">
                            <span className="text-sm font-medium">
                                {ticket.targetUser?.username?.charAt(0)?.toUpperCase() || '?'}
                            </span>
                        </div>
                    )}
                    <span className="text-sm">@{ticket.targetUser?.username || 'Unknown'}</span>
                </div>
            ),
        },
        {
            header: 'Tags',
            accessor: 'tags',
            render: (ticket) => (
                <div className="flex flex-wrap gap-1">
                    {ticket.tags?.slice(0, 2).map((tag) => {
                        const tagInfo = tags.find(t => t.name === tag);
                        return (
                            <span
                                key={tag}
                                className="px-2 py-0.5 text-xs rounded-full"
                                style={{
                                    backgroundColor: tagInfo?.color ? `${tagInfo.color}20` : '#6366f120',
                                    color: tagInfo?.color || '#6366f1'
                                }}
                            >
                                {tagInfo?.label || tag}
                            </span>
                        );
                    })}
                    {ticket.tags?.length > 2 && (
                        <span className="text-xs text-muted-light dark:text-muted-dark">
                            +{ticket.tags.length - 2}
                        </span>
                    )}
                </div>
            ),
        },
        {
            header: 'Status',
            accessor: 'status',
            render: (ticket) => (
                <Badge variant={getStatusBadgeVariant(ticket.status)}>
                    {getStatusLabel(ticket.status)}
                </Badge>
            ),
        },
        {
            header: 'Priority',
            accessor: 'priority',
            render: (ticket) => (
                <Badge variant={getPriorityBadgeVariant(ticket.priority)}>
                    {ticket.priority || 'Medium'}
                </Badge>
            ),
        },
        {
            header: 'Source',
            accessor: 'createdFrom',
            render: (ticket) => (
                <span className="text-sm capitalize">
                    {ticket.createdFrom === 'mobile' ? '📱 Mobile' : '🖥️ Admin'}
                </span>
            ),
        },
        {
            header: 'Created',
            accessor: 'createdAt',
            render: (ticket) => (
                <span className="text-sm text-muted-light dark:text-muted-dark">
                    {formatTimeAgo(ticket.createdAt)}
                </span>
            ),
        },
    ];

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold mb-2">Support Tickets</h1>
                    <p className="text-muted-light dark:text-muted-dark">
                        Manage and resolve user support tickets
                    </p>
                </div>
                <div className="flex gap-2">
                    <Button variant="secondary" onClick={() => refetch()}>
                        <RefreshCw size={18} />
                    </Button>
                    <Button variant="secondary" onClick={() => navigate('/support/tags')}>
                        <Tag size={18} />
                        Manage Tags
                    </Button>
                    <Button onClick={() => navigate('/support/create')}>
                        <Plus size={18} />
                        Create Ticket
                    </Button>
                </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-4 gap-4">
                <Card className="text-center">
                    <p className="text-3xl font-bold text-yellow">{stats.byStatus?.open || 0}</p>
                    <p className="text-sm text-muted-light dark:text-muted-dark">Open</p>
                </Card>
                <Card className="text-center">
                    <p className="text-3xl font-bold text-blue">{stats.byStatus?.in_progress || 0}</p>
                    <p className="text-sm text-muted-light dark:text-muted-dark">In Progress</p>
                </Card>
                <Card className="text-center">
                    <p className="text-3xl font-bold text-green">{stats.byStatus?.resolved || 0}</p>
                    <p className="text-sm text-muted-light dark:text-muted-dark">Resolved</p>
                </Card>
                <Card className="text-center">
                    <p className="text-3xl font-bold">{stats.total || 0}</p>
                    <p className="text-sm text-muted-light dark:text-muted-dark">Total</p>
                </Card>
            </div>

            {/* Filters and Table */}
            <Card>
                <div className="flex gap-4 mb-6">
                    <Select
                        value={status}
                        onChange={(e) => setStatus(e.target.value)}
                        className="w-40"
                    >
                        <option value="">All Status</option>
                        <option value="open">Open</option>
                        <option value="in_progress">In Progress</option>
                        <option value="resolved">Resolved</option>
                        <option value="closed">Closed</option>
                    </Select>
                    <Select
                        value={selectedTag}
                        onChange={(e) => setSelectedTag(e.target.value)}
                        className="w-40"
                    >
                        <option value="">All Tags</option>
                        {tags.map((tag) => (
                            <option key={tag.name} value={tag.name}>
                                {tag.label}
                            </option>
                        ))}
                    </Select>
                    <Select
                        value={createdFrom}
                        onChange={(e) => setCreatedFrom(e.target.value)}
                        className="w-40"
                    >
                        <option value="">All Sources</option>
                        <option value="mobile">Mobile</option>
                        <option value="admin_panel">Admin Panel</option>
                    </Select>
                </div>

                {isLoading ? (
                    <div className="flex justify-center py-12">
                        <LoadingSpinner size="large" />
                    </div>
                ) : (
                    <Table
                        columns={columns}
                        data={tickets}
                        onRowClick={(ticket) => navigate(`/support/${ticket._id}`)}
                        emptyState={{
                            title: 'No tickets found',
                            description: `No ${status || 'support'} tickets match your filters`,
                        }}
                    />
                )}
            </Card>
        </div>
    );
}

